<template>
  <div class="agree__wrap">
    <div class="agree__box">
      <div class="agree__item" :class="{ 'agree__item--no-divider': !isExpanded }">
        <c-checkbox
          :label="consentTitle"
          :model-value="isAllChecked"
          @update:model-value="emit('toggleAll', $event)"
        />
        <button
          type="button"
          class="agree__icon-button"
          :aria-expanded="isExpanded"
          :aria-label="isExpanded ? '약관 목록 접기' : '약관 목록 펼치기'"
          @click.stop="isExpanded = !isExpanded"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            class="chevron"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              :d="isExpanded ? ARROW_UP : ARROW_DOWN"
            />
          </svg>
        </button>
      </div>
      <template v-if="isExpanded && sortedTermsList.length > 0">
        <template v-for="term in sortedTermsList" :key="term.sn ?? term.title">
          <div class="agree__item">
            <c-checkbox :label="termLabel(term)" v-model="term.isCheck" />
            <button
              v-if="term.content"
              type="button"
              class="agree__icon-button"
              :aria-label="`${term.title} 약관 상세 보기`"
              @click.stop="emit('openDetail', term)"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                class="chevron"
              >
                <path fill-rule="evenodd" clip-rule="evenodd" :d="ARROW_RIGHT" />
              </svg>
            </button>
          </div>
          <div v-if="showMarketingHint && isMarketingTerm(term)" class="agree__marketing-hint">
            <template v-if="marketingHintText">✔ {{ marketingHintText }}</template>
            <template v-else>✔ <strong>혜택 및 이벤트 소식</strong>을 가장 먼저 알려드릴게요!</template>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { CCheckbox } from '@aj-fe/aj-vue-core'
import { isMarketingTerm } from '../counselTerms'
import type { TermItem } from '../types'
import { ARROW_DOWN, ARROW_RIGHT, ARROW_UP } from './icons'

const props = withDefaults(
  defineProps<{
    termsList: TermItem[]
    extraTermsList?: TermItem[]
    isAllChecked?: boolean
    showMarketingHint?: boolean
    consentTitle?: string
    marketingHintText?: string
  }>(),
  {
    extraTermsList: () => [],
    isAllChecked: false,
    showMarketingHint: false,
    consentTitle: '전체 동의',
    marketingHintText: '',
  },
)

const emit = defineEmits<{
  (e: 'toggleAll', value: boolean): void
  (e: 'openDetail', term: TermItem): void
}>()

const isExpanded = ref(true)

// 필수/선택 표기는 약관 데이터의 isRequired 로만 만든다 (서버 약관·상조 약관 공통 — title 에 문구를 박지 않는다)
function termLabel(term: TermItem): string {
  return `${term.isRequired ? '(필수)' : '(선택)'} ${term.title}`
}

const sortedTermsList = computed(() => {
  const termsArray = Array.isArray(props.termsList) ? props.termsList : []
  const all: TermItem[] = [...termsArray, ...props.extraTermsList]
  return all.sort((a, b) => (b.isRequired ? 1 : 0) - (a.isRequired ? 1 : 0))
})
</script>

<style lang="scss">
.chevron {
  fill: var(--asf-consent-chevron, #acb3ba);
}

.agree__box {
  border: 1px solid var(--asf-consent-border, var(--c-gray300, #cfd4d9));
  border-radius: var(--asf-consent-radius, 12px);
  background: var(--asf-consent-bg, transparent);
  padding: 20px;
  margin-top: 32px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .agree__item {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:first-child {
      padding-bottom: 20px;
      border-bottom: 1px solid var(--c-gray200, #dee2e6);
      margin-bottom: 8px;

      &.agree__item--no-divider {
        padding-bottom: 0;
        border-bottom: none;
        margin-bottom: 0;
      }
    }

    &:not(:first-child) {
      color: var(--c-gray700, #575e64);
    }
  }

  .agree__icon-button {
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
  }

  .agree__item:has(+ .agree__marketing-hint) {
    padding-bottom: 0;
    margin-bottom: -4px;
  }

  .agree__marketing-hint {
    padding-left: 32px;
    font-size: 0.8125rem;
    color: var(--c-gray900, #1f2429);

    strong {
      color: var(--c-primary600, #145ce6);
    }
  }
}
</style>
